// FIXME
// eslint-disable-next-line workspaces/require-dependency
import { getConfluencePageAriFromContentRef } from '@databases/connections/utils/ariHelper';
import { ContentFieldToDisplayedDetailType } from '@databases/model/field';
// eslint-disable-next-line workspaces/require-dependency
import { type DefaultRootState } from '@databases/redux';
// eslint-disable-next-line workspaces/require-dependency
import { loadContent } from '@databases/redux/connections/actions';
// eslint-disable-next-line workspaces/require-dependency
import { loadContentExcerpt } from '@databases/redux/connections/actions/loadExcerpt';
// eslint-disable-next-line workspaces/require-dependency
import { loadContentVersionComment } from '@databases/redux/connections/actions/loadVersionComment';
// eslint-disable-next-line workspaces/require-dependency
import {
  getContentForReference,
  selectContentOptional,
} from '@databases/redux/connections/selectors';
import { type ObjectMode, type Optional } from '@databases/types';
import {
  type DatabaseField,
  DatabaseFieldType,
  DisplayedDetailType,
  type FieldWithContext,
  type RawValueWithContext,
} from '@databases/types/database';
import { DatabaseFilterType } from '@databases/types/database/view';

import { loadContentById } from '../../redux/content/actions';
import type { FieldType } from '../field-types';
import {
  getConvertedDatabaseValue,
  getDatabaseEntryOptional,
  getDatabaseFieldOptional,
  isBrokenDatabase,
} from '../utils';

import { filterValidPageLinkValue, isExistingPageLinkValue } from './pageLink';

/**
 * Returns the page link field in the same database that the page status field references.
 */
function getReferencedPageField({
  database,
  field,
}: FieldWithContext<DatabaseFieldType, ObjectMode>):
  | DatabaseField<DatabaseFieldType.PAGE_LINK>
  | undefined {
  const referencedField = getDatabaseFieldOptional(database, field.settings.fieldId);
  if (referencedField?.type !== DatabaseFieldType.PAGE_LINK) {
    return undefined;
  } else {
    return referencedField as DatabaseField<DatabaseFieldType.PAGE_LINK>;
  }
}

function renderExportView(
  {
    database,
    field,
    entry,
  }: Optional<RawValueWithContext<DatabaseFieldType.PAGE_DETAILS, ObjectMode>, 'entry'>,
  state: DefaultRootState
): string {
  return getContentForReference(database, field, entry, state)
    .flatMap((content) => {
      const contentField = field.settings.contentField || field.settings.displayedDetail;
      return content['data'][contentField]?.text ?? '';
    })
    .join('<br/>');
}

function getStringRepresentation(
  {
    database,
    field,
    entry,
  }: Optional<RawValueWithContext<DatabaseFieldType.PAGE_DETAILS, ObjectMode>, 'entry'>,
  state: DefaultRootState | undefined
): string {
  if (!state) {
    return '';
  }

  return getContentForReference(database, field, entry, state)
    .flatMap((content) => {
      const contentField = field.settings.contentField || field.settings.displayedDetail;
      return content['data'][contentField]?.text ?? '';
    })
    .join('\n');
}

const pageDetailsFieldType: FieldType<DatabaseFieldType.PAGE_DETAILS> = {
  supportedFilterTypes: [DatabaseFilterType.CONTAINS],

  isCsvImportSupported: false,

  getInitialValue: () => ({}),

  isEmptyValue: (context, state) => getStringRepresentation(context, state) === '',

  isEqualValue: (context1, context2, state) => {
    if (!state) {
      return true;
    }

    return renderExportView(context1, state) === renderExportView(context2, state);
  },

  renderExportView,

  getStringRepresentation,

  loadDependencies: (
    { contentId, field, entryId },
    { dispatch, selector, selectDatabase }
  ) => {
    const referencedField = selector((state) => {
      const _db = selectDatabase(state, { contentId });
      const database = _db && !isBrokenDatabase(_db) ? _db : undefined;
      return database && getReferencedPageField({ database, field });
    });

    const referencedContent = selector((state) => {
      const _db = selectDatabase(state, { contentId });
      const database = _db && !isBrokenDatabase(_db) ? _db : undefined;
      const entry =
        database && entryId ? getDatabaseEntryOptional(database, entryId) : undefined;
      const value =
        database &&
        referencedField &&
        entry &&
        getConvertedDatabaseValue(database, referencedField, entry, state);
      return (
        value &&
        filterValidPageLinkValue(value.value).filter((contentRef) =>
          isExistingPageLinkValue(contentRef)
        )
      );
    });

    if (referencedContent) {
      for (const contentRef of referencedContent) {
        if (!contentRef.contentId) {
          continue;
        }

        dispatch(loadContentById(contentRef.contentId));

        const ari = getConfluencePageAriFromContentRef(contentRef);
        if (ari && referencedField) {
          dispatch(
            loadContent({
              ari,
              connectionId: referencedField.id,
            })
          );
        }
      }
    }

    const connectionContent = selector((state) => {
      return referencedContent?.map((content) => {
        if (referencedContent && 'contentId' in content) {
          const ari = getConfluencePageAriFromContentRef(content);
          if (ari) {
            return selectContentOptional(state.connections, ari);
          }
        }

        return null;
      });
    });

    for (const content of connectionContent ?? []) {
      if (content) {
        if (field.settings.contentField === DisplayedDetailType.EXCERPT) {
          dispatch(loadContentExcerpt({ ari: content.ari }));
        }

        if (
          field.settings.contentField ===
            ContentFieldToDisplayedDetailType.VERSION_NUMBER &&
          field.settings.includeVersionComment
        ) {
          dispatch(loadContentVersionComment({ ari: content.ari }));
        }
      }
    }
  },

  noDefaultValue: true,
};

export default pageDetailsFieldType;
